/**
 * `dependencyGraph` — the pure DAG layer that sits ON TOP of the tree (§A5).
 * Dependency-free, no rendering, no data fetching.
 *
 * Issue #766 (Phase 4). Decomposition and dependency are kept apart
 * (§A5): the TREE is `parent_id` (solid connectors, laid out by
 * `layoutTree`); the DEPENDENCY layer is a separate set of edges that may
 * cross branches — a directed acyclic graph rendered over the tree. This
 * module owns the *graph maths* for that second layer so the canvas overlay
 * and the drawer rows share one source of truth, and so the tree's own
 * cycle-check (`outlineModel.wouldCreateCycle`, over `parent_id`) is never
 * confused with the dependency cycle-check (over these edges).
 *
 * Direction convention (matches the backend `CreateDependencyRequest`):
 *
 *     from ──▶ to     "from COMES BEFORE to"  (to DEPENDS ON from)
 *
 * So for a node N:
 *   - "Comes after" = the edges where `to == N` (the things N waits on);
 *   - "Blocks"      = the edges where `from == N` (the things waiting on N).
 *
 * The arrow on the canvas always points at the *later* task (`to`).
 */

/** One directed edge — the relevant subset of the API's `TaskDependencyRead`. */
export interface DependencyEdge {
  id: string;
  from_node_id: string;
  to_node_id: string;
}

/**
 * The dependencies a node sits ON THE END of — i.e. the edges where this
 * node is the `to` (the dependent). Reads as "Comes after …" in the drawer.
 */
export function comesAfter<E extends DependencyEdge>(edges: readonly E[], nodeId: string): E[] {
  return edges.filter((e) => e.to_node_id === nodeId);
}

/**
 * The dependencies a node sits AT THE START of — i.e. the edges where this
 * node is the `from` (the prerequisite). Reads as "Blocks …" in the drawer.
 */
export function blocks<E extends DependencyEdge>(edges: readonly E[], nodeId: string): E[] {
  return edges.filter((e) => e.from_node_id === nodeId);
}

/**
 * Would adding `from ──▶ to` create a directed cycle among the existing
 * edges (or is it a self-loop)? Mirrors the backend's `TaskCycleError` guard
 * so the canvas / drawer can reject an illegal link *before* a round-trip —
 * snappier, and it avoids a doomed POST. The server remains the source of
 * truth (it re-checks and returns 422); this is the same "check locally,
 * trust the server" posture as the tree's `wouldCreateCycle`.
 *
 * A new edge `from ──▶ to` closes a cycle exactly when `to` can already
 * reach `from` by following existing edges (because then `from ──▶ to ──▶ …
 * ──▶ from`). A self-loop (`from == to`) is trivially a cycle.
 */
export function wouldCreateDependencyCycle(
  edges: readonly DependencyEdge[],
  fromNodeId: string,
  toNodeId: string,
): boolean {
  if (fromNodeId === toNodeId) return true;
  return isReachable(edges, toNodeId, fromNodeId);
}

/**
 * Is `target` reachable from `start` by following edges in their direction
 * (`from → to`)? A bounded DFS over the adjacency built from the edge list.
 * Visited-tracking keeps it terminating even if the stored data ever held a
 * cycle (it shouldn't — the backend forbids it).
 */
function isReachable(
  edges: readonly DependencyEdge[],
  startNodeId: string,
  targetNodeId: string,
): boolean {
  const adjacency = new Map<string, string[]>();
  for (const edge of edges) {
    const outgoing = adjacency.get(edge.from_node_id);
    if (outgoing) outgoing.push(edge.to_node_id);
    else adjacency.set(edge.from_node_id, [edge.to_node_id]);
  }

  const visited = new Set<string>();
  const stack = [startNodeId];
  while (stack.length > 0) {
    const current = stack.pop() as string;
    if (current === targetNodeId) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    const next = adjacency.get(current);
    if (next) stack.push(...next);
  }
  return false;
}
